import { useState, useCallback, useRef, useEffect } from 'react'

export const useFileDragAndDrop = (onDrop, acceptedFileTypes = [], onError = null) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const dropZoneRef = useRef(null)

  // Global drag event handlers to detect when drag leaves the entire window
  useEffect(() => {
    const handleGlobalDragEnter = (e) => {
      // Only activate for external file drags (files from outside the browser)
      const hasFiles = e.dataTransfer.types.includes('Files')
      const hasFileItems = e.dataTransfer.items && Array.from(e.dataTransfer.items).some(item => item.kind === 'file')
      
      if (hasFiles || hasFileItems) {
        e.preventDefault()
        setIsDragOver(true)
      }
    }

    const handleGlobalDragLeave = (e) => {
      // Check if we're leaving the entire window (not just moving between elements)
      if (e.clientX <= 0 || e.clientY <= 0 || 
          e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        setIsDragOver(false)
      }
    }

    const handleGlobalDrop = () => {
      // Reset drag state when files are dropped anywhere
      setIsDragOver(false)
    }

    // Add global event listeners
    document.addEventListener('dragenter', handleGlobalDragEnter)
    document.addEventListener('dragleave', handleGlobalDragLeave)
    document.addEventListener('dragover', handleGlobalDragOver)
    document.addEventListener('drop', handleGlobalDrop, true)

    return () => {
      document.removeEventListener('dragenter', handleGlobalDragEnter)
      document.removeEventListener('dragleave', handleGlobalDragLeave)
      document.removeEventListener('dragover', handleGlobalDragOver)
      document.removeEventListener('drop', handleGlobalDrop, true)
    }
  }, [])

  const handleGlobalDragOver = (e) => {
    const hasFiles = e.dataTransfer.types.includes('Files') ||
      (e.dataTransfer.items && Array.from(e.dataTransfer.items).some(item => item.kind === 'file'))
    if (hasFiles) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDragEnter = useCallback((e) => {
    // Only activate for external file drags (files from outside the browser)
    const hasFiles = e.dataTransfer.types.includes('Files')
    const hasFileItems = e.dataTransfer.items && Array.from(e.dataTransfer.items).some(item => item.kind === 'file')
    
    // If no files are being dragged, ignore this event
    if (!hasFiles && !hasFileItems) {
      return
    }
    
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if(e.relatedTarget === null) {
      setIsDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e) => {
    // Only handle file drags from outside
    const hasFiles = e.dataTransfer.types.includes('Files')
    const hasFileItems = e.dataTransfer.items && Array.from(e.dataTransfer.items).some(item => item.kind === 'file')
    
    if (!hasFiles && !hasFileItems) {
      return
    }
    
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    // Only handle file drops from outside
    const files = Array.from(e.dataTransfer.files)
    
    if (files.length === 0) {
      return
    }
    
    e.preventDefault()
    e.stopPropagation()
    
    setIsDragOver(false)

    // Filter files by accepted types if specified
    const filteredFiles = acceptedFileTypes.length > 0 
      ? files.filter(file => {
          const fileType = file.type.split('/')[1]?.toLowerCase()
          return acceptedFileTypes.some(type => 
            type.toLowerCase() === fileType || 
            file.name.toLowerCase().endsWith(`.${type.toLowerCase()}`)
          )
        })
      : files

    if (filteredFiles.length > 0) {
      onDrop(filteredFiles)
    } else if (acceptedFileTypes.length > 0 && files.length > 0) {
      // Show error if files were dropped but none matched the accepted types
      const acceptedTypesText = acceptedFileTypes.join(', ')
      const errorMessage = `サポートされていないファイル形式です。\n対応形式: ${acceptedTypesText}`
      if (onError && typeof onError === 'function') {
        onError(errorMessage)
      }
    }
  }, [onDrop, acceptedFileTypes, onError])

  const getDropZoneProps = useCallback(() => ({
    ref: dropZoneRef,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
  }), [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  return {
    isDragOver,
    getDropZoneProps,
    dropZoneRef
  }
}

